import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Package, ArrowRight } from "lucide-react";
import type { TemplateStack } from "@/lib/template-utils";

interface TemplateStackCardProps {
  stack: TemplateStack;
  onView: (stack: TemplateStack) => void;
}

export function TemplateStackCard({ stack, onView }: TemplateStackCardProps) {
  const templateCount = stack.templates.length;
  const toTitleCase = (value?: string | null) => {
    if (!value) return '';
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Card className="shadow-card hover:shadow-elevated transition-shadow transition-colors hover:bg-muted/60 relative overflow-hidden flex flex-col h-full">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-bl-full" />
      
      <CardHeader className="relative pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="text-lg leading-tight flex items-center gap-2 mb-2">
              <Package className="h-5 w-5 text-primary" />
              {stack.name}
            </CardTitle>
            {stack.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {stack.description}
              </p>
            )}
          </div>
          <Badge variant="secondary" className="shrink-0">
            {templateCount} {templateCount === 1 ? 'template' : 'templates'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="relative space-y-3 p-6 pt-0">
        <div className="flex flex-wrap gap-2">
          {stack.category && (
            <Badge variant="outline" className="text-xs">
              {toTitleCase(stack.category)}
            </Badge>
          )}
          {stack.compliance_framework && (
            <Badge variant="default" className="text-xs">
              {stack.compliance_framework}
            </Badge>
          )}
        </div>
      </CardContent>

      <div className="pt-4 border-t px-6 pb-4 mt-auto">
        <Button
          onClick={() => onView(stack)}
          className="w-full"
          size="sm"
        >
          View Stack Details
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );
}

